// Simple API client for making HTTP requests using axios
import axios from 'axios';
import { toast } from 'sonner';

// Global error handler - will be set by the app
let globalErrorHandler = null;

export const setGlobalErrorHandler = (handler) => {
  globalErrorHandler = handler;
};

// Get the base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_UDYOG_VRIKSH_API;

// Validate that the API base URL is configured
if (!API_BASE_URL) {
  console.error('❌ REACT_APP_UDYOG_VRIKSH_API is not defined...');

  // In development, throw an error to catch this early
  if (process.env.NODE_ENV === 'development') {
    throw new Error('Missing required environment variable: REACT_APP_UDYOG_VRIKSH_API');
  }
}

// Memory storage for access token
let accessToken = null;

export const setAccessToken = (token) => {
  accessToken = token;
};

export const getAccessToken = () => {
  return accessToken;
};

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // Set a timeout for requests (10 seconds)
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for HttpOnly cookies
});

// Request interceptor to automatically attach JWT token from memory
axiosInstance.interceptors.request.use(
  (config) => {
    // Don't attach token to refresh endpoint to avoid using expired tokens
    if (accessToken && !config.url.includes('/user/refresh')) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag to prevent multiple concurrent refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Response interceptor for silent refresh
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Keep track of retry to prevent infinite loops
    if (!originalRequest._retry) {
      originalRequest._retry = false;
    }

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/user/login') &&
      !originalRequest.url.includes('/user/refresh')
    ) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise(function (resolve, reject) {
        axiosInstance
          .post('/user/refresh')
          .then(({ data }) => {
            const newAccessToken = data.jwtToken;
            setAccessToken(newAccessToken);

            // Update the header for the original request
            originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;

            processQueue(null, newAccessToken);
            resolve(axiosInstance(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            // Trigger logout on refresh failure
            window.dispatchEvent(new CustomEvent('auth:logout'));
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    // Handle network failures and server errors globally
    // NOTE: Global error screen disabled for API failures
    // Users will see regular error handling (toasts, inline errors, etc.)
    // Global error screen only shows for offline detection

    if (!error.response) {
      // Network error - no response received
      // Commented out to prevent full-screen error overlay
      // if (globalErrorHandler) {
      //   globalErrorHandler(
      //     'This issue is on our side. Our developers are working on it. Please try later or reinstall the app.'
      //   );
      // }
      console.error('[API] Network error:', error.message);
    } else if (error.response.status >= 500) {
      // Server error (5xx)
      // Commented out to prevent full-screen error overlay
      // if (globalErrorHandler) {
      //   globalErrorHandler('Server is temporarily unavailable. Please try again later.');
      // }
      console.error('[API] Server error:', error.response.status);
    }

    return Promise.reject(error);
  }
);

// Function to make API requests
export const fetchApi = async (endpoint, method, body, options = {}) => {
  const { showErrorToast = true } = options;

  // Set up the request config
  const config = {
    method: method,
    url: endpoint,
  };

  // Add body if provided (for POST, PUT requests)
  if (body) {
    config.data = body; // axios automatically stringifies the data
  }

  try {
    // Make the request
    const response = await axiosInstance(config);

    // Return success response
    return {
      status: true,
      data: response.data,
    };
  } catch (error) {
    let errorMsg = 'Request failed. Please try again.';

    // Handle error responses
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      errorMsg = error.response.data.message || 'Request failed. Please try again.';
    } else if (error.request) {
      // The request was made but no response was received
      errorMsg = 'Server not responding. Please try again.';
    } else {
      // Something happened in setting up the request that triggered an Error
      errorMsg = 'Network error. Check connection.';
    }

    // Show toast for error
    if (!error.config?._retry) { // Don't show toast if we are retrying (silent refresh)? 
      // Actually, if refresh fails, we might want to show error or just logout.
      // If final failure, show error.
      // But fetchApi wraps the call. if axiosInstance throws, we get here.
      // If refresh works, axiosInstance returns success, we go to try block.
    }

    if (showErrorToast && endpoint !== '/user/auth/me') {
      // Don't show error toast for auth check on load
      toast.error(errorMsg);
    }

    return {
      status: false,
      errorMsg: errorMsg,
    };
  }
};
